import { LoadingSpinner } from "components/LoadingSpinner";
import { orderTypeConstants } from "constants/order";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import { useFinixPaymentProvider } from "hooks/useFinixPaymentProvider";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  IRefundableCard,
  useGetRefundableCardsQuery,
} from "store/queries/moneyTransfers";
import { userSelector } from "store/slices/user";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";

import {
  buildProviderTotals,
  describeFinixCardRefundLimit,
  describeFinixClearHealthRefundLimit,
  finixCardRefundForProvider,
  finixClearHealthRefund,
  reversibleForProviderCardInCents,
} from "../utils/helpers";

interface ITransfer {
  selectedPartyToTransferFrom: {
    name: string;
    id: string;
    totalAmount: number;
    type: string;
  } | null;
  selectedPartyToTransferTo: {
    name: string;
    id: string;
    totalAmount: number;
    type: string;
  } | null;
  selectedAmountToTransfer: number;
  withholdClearFee?: boolean;
  // CLE-2458: chosen source Payment.id when refunding a split (multi-card) payment to the patient.
  selectedRefundCardPaymentId?: string;
}
export const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

export function SingleTransferForm({
  order,
  transfer,
  transfers,
  setTransfers,
  index,
  handleRemoveTransfer,
  showRemoveButton,
}: {
  order: IOrder;
  transfer: ITransfer;
  transfers: ITransfer[];
  setTransfers: any;
  index: number;
  showRemoveButton: boolean;
  handleRemoveTransfer: (index: number) => void;
}) {
  const { accounts } = useUserWithAccount();
  const user = useSelector(userSelector);
  const isSuperAdmin = user.scope === UserRole.SUPER_ADMIN;
  // Strict-freeze: the money-transfer form's rail-specific behavior follows the
  // rail this order is BOUND to (authoritative endpoint via the hook), not the
  // account's current useFinix flag. Error-fallback policy: this form moves real
  // money, so when the rail can't be determined (endpoint loading OR errored) we
  // FAIL CLOSED rather than guess from the account flag — see the gate below.
  const {
    isFinix: useFinix,
    isLoading: isProviderLoading,
    isError: isProviderError,
  } = useFinixPaymentProvider(order);

  // CLE-2458: distinct cards the patient used to pay. Returns 2+ only for split (multi-card)
  // payments, so the "Refund to card" picker appears only when there's actually a choice.
  const { data: refundableCardsResponse } = useGetRefundableCardsQuery(
    order.id
  );
  const refundableCards = refundableCardsResponse?.data || [];
  const toIsPatient = transfer.selectedPartyToTransferTo?.type === "patient";
  const showRefundCardPicker = toIsPatient && refundableCards.length >= 2;
  // CLE-2634: how much a card can still take back comes from the server, which reads it from
  // Stripe. This used to be derived here by subtracting money-transfer items that carried a
  // sourcePaymentId — which made every refund NOT pinned to a card (auto-selected, pre-CLE-2458,
  // or issued straight from the Stripe dashboard) invisible, so the form happily offered a cap the
  // card had no room for and the transfer failed after the fact. Null on Finix, where the binding
  // limit is the provider∩card slice computed below instead.
  const getCardRemainingInCents = (card?: IRefundableCard) =>
    card?.remainingRefundableInCents ?? undefined;
  // CLE-2458: a refund pinned to a card can't exceed what that card can still take back, so this is
  // the upper bound for the amount field while a specific card is selected.
  const selectedRefundCard = refundableCards.find(
    (card) => card.paymentId === transfer.selectedRefundCardPaymentId
  );
  // CLE-2458: cards already pinned by OTHER transfers in this batch. Each card can only absorb its
  // own charges, so two transfers must not both target the same card — disable those options here.
  const cardsChosenByOtherTransfers = transfers
    .filter((_, i) => i !== index)
    .map((t) => t.selectedRefundCardPaymentId)
    .filter(Boolean);
  // CLE-2458: the (provider ∩ card) reversible — see reversibleForProviderCardInCents.
  const getProviderCardReversibleInCents = (
    providerId?: string | null,
    cardPaymentId?: string
  ): number | undefined =>
    reversibleForProviderCardInCents({ order, providerId, cardPaymentId });
  // The (provider ∩ card) limit only binds on FINIX, where a patient refund REVERSES the specific
  // provider transfer the card funded — so the card can only get back what that provider held.
  // On STRIPE the refund hits the card's original charge directly; "how much of each card went to
  // each provider" is only our local ProviderStripeTransfer bookkeeping, not something Stripe
  // enforces (patient→ClearHealth and ClearHealth→provider are separate transfers). So a Stripe card
  // refund is bounded by the card's full charge (plus the provider's total reversible), not the slice.
  const fromIsClearHealth =
    !!transfer.selectedPartyToTransferFrom &&
    transfer.selectedPartyToTransferFrom.id === null;
  // CLE-2993: on Finix, Clear Health can refund the patient only from its own leg of the card sales (the
  // Clear Patient Fee / Bundled upcharge / surcharge split to the Clear Health merchant), capped at what is
  // left of it. `clearHealthRefundableInCents` is the whole order's figure (it decides whether Clear Health
  // is offered at all); `getClearHealthRefundCap` narrows it to a pinned card.
  const clearHealthRefundableInCents = useFinix
    ? finixClearHealthRefund({ order })
    : 0;
  const getClearHealthRefundCap = (cardPaymentId?: string) =>
    useFinix && fromIsClearHealth
      ? finixClearHealthRefund({ order, cardPaymentId })
      : undefined;
  const clearHealthRefundCapInCents = getClearHealthRefundCap(
    selectedRefundCard?.paymentId
  );
  const providerCardReversibleInCents = getProviderCardReversibleInCents(
    transfer.selectedPartyToTransferFrom?.id,
    selectedRefundCard?.paymentId
  );
  // The per-provider slice is the binding card limit only on Finix when refunding from a provider.
  const usesProviderCardCap =
    useFinix &&
    !fromIsClearHealth &&
    !!transfer.selectedPartyToTransferFrom?.id;
  // The max refundable to the chosen card given the current "From" party and rail. On Finix from a
  // provider it's the (provider ∩ card) reversible (already net of reversals); otherwise it's the
  // remaining refundable the server read from Stripe for that card's charges.
  const selectedCardCapInCents = (() => {
    if (!selectedRefundCard) return undefined;
    if (usesProviderCardCap) return providerCardReversibleInCents;
    // CLE-2993: on Finix, Clear Health can refund a card only from that sale's Clear Health leg.
    if (clearHealthRefundCapInCents != null) return clearHealthRefundCapInCents;
    return getCardRemainingInCents(selectedRefundCard);
  })();
  // CLE-2997: on Finix, a refund to the patient can only reverse card SALES. Money a provider received
  // by transfer (provider-to-provider, Clear-to-provider) counts in its balance but can't go back to the
  // card — the server refuses it — so the amount is capped at what can, and the form names the
  // provider(s) to transfer to first.
  const getFinixCardRefund = (
    providerId?: string | null,
    cardPaymentId?: string
  ) =>
    useFinix && providerId
      ? finixCardRefundForProvider({ order, providerId, cardPaymentId })
      : undefined;
  const finixCardRefund = toIsPatient
    ? getFinixCardRefund(
        transfer.selectedPartyToTransferFrom?.id,
        selectedRefundCard?.paymentId
      )
    : undefined;
  const cardRefundIsLimited =
    !!finixCardRefund &&
    finixCardRefund.refundableInCents < finixCardRefund.balanceInCents;
  // Effective max for the amount field: the smaller of the provider's total reversible amount,
  // (when a card is pinned) the max refundable to that card, and (CLE-2997) what can go back to the card.
  const amountMaxInCents = (() => {
    const providerCap = transfer.selectedPartyToTransferFrom?.id
      ? transfer.selectedPartyToTransferFrom.totalAmount
      : undefined;
    const caps = [
      providerCap,
      selectedCardCapInCents,
      finixCardRefund?.refundableInCents,
      clearHealthRefundCapInCents,
    ].filter((value): value is number => value != null);
    return caps.length ? Math.min(...caps) : undefined;
  })();
  // A provider source with nothing left to reverse can't fund any transfer, so the amount field is
  // locked at $0.00 and says why instead of accepting digits the server would refuse.
  const sourceHasNothingAvailable =
    !!transfer.selectedPartyToTransferFrom?.id &&
    amountMaxInCents != null &&
    amountMaxInCents <= 0;

  const paymentBreakdownDetails = useMemo(() => {
    return order?.paymentFullBreakdown?.bundledProviderAmounts;
  }, [order]);
  const paymentPlanPayments = useMemo(() => {
    return order?.paymentPlanBreakdown?.payments;
  }, [order]);
  const providerStripeTransfers = useMemo(() => {
    return order?.providerStripeTransfers;
  }, [order]);

  const providerFinixTransfers = useMemo(() => {
    return order?.providerFinixTransfers;
  }, [order]);

  const providerTotals = useMemo(
    () =>
      buildProviderTotals({ providerStripeTransfers, providerFinixTransfers }),
    [providerStripeTransfers, providerFinixTransfers]
  );

  const eligibleToProviders = useMemo(() => {
    const providersInOrder: {
      [key: string]: { name: string; id: string; type: string };
    } = {};
    const otherProvidersInAccount: {
      [key: string]: { name: string; id: string; type: string };
    } = {};
    if (paymentPlanPayments) {
      // for payment plans, only allow refunds of payments that have already been transferred to providers
      paymentPlanPayments?.forEach((payment) => {
        if (payment.transferredToProviders) {
          payment.providerBreakdown.forEach((amount: any) => {
            if (!providersInOrder[amount.providerId]) {
              providersInOrder[amount.providerId] = {
                name: amount.providerName,
                id: amount.providerId,
                type: "provider",
              };
            }
          });
        }
      });
    } else {
      // no payment plan
      paymentBreakdownDetails?.forEach((amount) => {
        if (!providersInOrder[amount.providerId]) {
          providersInOrder[amount.providerId] = {
            name: amount.providerName,
            id: amount.providerId,
            type: "provider",
          };
        }
      });
    }
    const accountWithProviders = accounts?.data?.find(
      (account) => account.id === order.accountId
    );
    if (accountWithProviders?.providers) {
      [...accountWithProviders.providers]
        .filter(
          (provider) =>
            !!provider.stripeAccountNumber || !!provider.finixMerchantId
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((provider) => {
          if (!otherProvidersInAccount[provider.id]) {
            otherProvidersInAccount[provider.id] = {
              name: provider.name,
              id: provider.id,
              type: "provider",
            };
          }
        });
    }

    return {
      providersInOrder: Object.values(providersInOrder),
      otherProvidersInAccount: Object.values(otherProvidersInAccount),
    };
  }, [accounts, order, paymentPlanPayments, paymentBreakdownDetails]);

  const transferFromOptions = useMemo(() => {
    // On Finix, Clear Health can originate a transfer only when it holds some of the patient's money on
    // this order — its CLEARHEALTH_UPCHARGE leg of a card sale (CLE-2993) — and then only a refund to the
    // patient. Under the APPLICATION_FEE-only model the Clear Health merchant is not in the sale's
    // split_transfers and cannot originate a reversal, so those orders keep it hidden.
    const clearHealthOption = {
      name: "Clear Health",
      id: null,
      totalAmount: useFinix ? clearHealthRefundableInCents : null,
      type: "provider",
    };
    const offerClearHealth = !useFinix || clearHealthRefundableInCents > 0;
    return [
      ...providerTotals,
      ...(offerClearHealth ? [clearHealthOption] : []),
    ];
  }, [providerTotals, useFinix, clearHealthRefundableInCents]);

  const transferToOptions = useMemo(() => {
    // Create grouped options structure with empty array fallbacks
    const providersInOrder = eligibleToProviders?.providersInOrder || [];
    const otherProvidersInAccount =
      eligibleToProviders?.otherProvidersInAccount || [];

    // Check if any payment on the order was made by check
    const hasCheckPayment = order.payments?.some(
      (payment) => payment.type === "Check"
    );

    // CLE-2993: on a Finix order Clear Health can refund the patient only from its own leg of the card
    // sales. With no such leg (the APPLICATION_FEE-only model) a reversal naming Clear Health fails Finix
    // validation, so the patient option stays hidden; with one, the patient is the only destination
    // offered from Clear Health (Clear → provider stays unavailable from this dialog on Finix, as before).
    const fromIsClearHealth = transfer.selectedPartyToTransferFrom?.id === null;
    const hideClearToPatient =
      useFinix && fromIsClearHealth && clearHealthRefundableInCents <= 0;
    const clearHealthToPatientOnly = useFinix && fromIsClearHealth;

    // Build patient and Clear Health items, excluding patient if payment was by check
    // OR when CLEAR_TO_PATIENT is disabled (Finix accounts with from=Clear Health)
    const patientAndClearHealthItems = [
      ...(!hasCheckPayment && !hideClearToPatient
        ? [
            {
              name: `${order.patient.firstName} ${order.patient.lastName}`,
              id: order.patientId,
              type: "patient",
            },
          ]
        : []),
      { name: "Clear Health", id: null, type: "provider" },
    ];

    if (clearHealthToPatientOnly) {
      return [
        {
          label: "Patient",
          items: patientAndClearHealthItems.filter(
            (item) => item.type === "patient"
          ),
          type: "group",
        },
      ];
    }

    // Everything must be in groups for consistency
    return [
      {
        label: "Patient and Clear Health",
        items: patientAndClearHealthItems,
        type: "group",
      },
      ...(providersInOrder.length > 0
        ? [
            {
              label: "Providers in this estimate",
              items: providersInOrder,
              type: "group",
            },
          ]
        : []),
      ...(otherProvidersInAccount.length > 0
        ? [
            {
              label: "Other providers in account",
              items: otherProvidersInAccount,
              type: "group",
            },
          ]
        : []),
    ];
  }, [
    order,
    eligibleToProviders,
    transfer.selectedPartyToTransferFrom,
    useFinix,
    clearHealthRefundableInCents,
  ]);

  const itemTemplate = (item: any) => {
    if (item.type === "group") {
      return null;
    }
    return (
      <div>
        {item.name}{" "}
        {item.totalAmount ? parsePrice(centsToDollars(item.totalAmount)) : ""}
      </div>
    );
  };

  const optionGroupTemplate = (option: any) => {
    return (
      <div className="flex align-items-center text-primary">{option.label}</div>
    );
  };

  const valueTemplate = (option: any, props: any) => {
    return option ? (
      <div>
        {option.name}{" "}
        {option.totalAmount
          ? parsePrice(centsToDollars(option.totalAmount))
          : ""}
      </div>
    ) : (
      // eslint-disable-next-line react/prop-types
      <div>{props.placeholder}</div>
    );
  };

  const onTransferFromChange = (selectedParty: any) => {
    const isClearHealth = selectedParty.id === null;
    const totalAllowable = selectedParty?.totalAmount;
    const currentAmount = transfer.selectedAmountToTransfer;
    if (isClearHealth) {
      // CLE-2993: on Finix, Clear Health can only refund the patient, and only up to what it holds on the
      // order (on the pinned card, if one is chosen). Clear a "to" that is not the patient and clamp the
      // amount. Off Finix nothing changes.
      const toIsPatientNow =
        transfer.selectedPartyToTransferTo?.type === "patient";
      const shouldClearTo = useFinix && !toIsPatientNow;
      const clearHealthCap = useFinix
        ? finixClearHealthRefund({
            order,
            cardPaymentId: toIsPatientNow
              ? selectedRefundCard?.paymentId
              : undefined,
          })
        : undefined;
      setTransfers((prevTransfers: any) => {
        const updatedTransfers = [...prevTransfers];
        const current = updatedTransfers[index];
        updatedTransfers[index] = {
          ...current,
          selectedPartyToTransferFrom: selectedParty,
          ...(shouldClearTo
            ? {
                selectedPartyToTransferTo: null,
                selectedRefundCardPaymentId: undefined,
              }
            : {}),
          ...(clearHealthCap != null &&
          current.selectedAmountToTransfer > clearHealthCap
            ? { selectedAmountToTransfer: clearHealthCap }
            : {}),
        };
        return updatedTransfers;
      });
      return;
    }
    let updatedAmount =
      currentAmount > totalAllowable ? totalAllowable : currentAmount;
    // CLE-2997: a patient refund from the new provider can't exceed what can go back to the card.
    const cardRefundCap = toIsPatient
      ? getFinixCardRefund(selectedParty.id, selectedRefundCard?.paymentId)
          ?.refundableInCents
      : undefined;
    if (cardRefundCap != null && updatedAmount > cardRefundCap) {
      updatedAmount = cardRefundCap;
    }
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        selectedPartyToTransferFrom: selectedParty,
        selectedAmountToTransfer: updatedAmount,
      };
      return updatedTransfers;
    });
  };

  const onTransferToChange = (selectedParty: any) => {
    // CLE-2997: choosing the patient caps an amount already entered at what can go back to the card.
    const cardRefundCap =
      selectedParty?.type === "patient"
        ? getFinixCardRefund(transfer.selectedPartyToTransferFrom?.id)
            ?.refundableInCents
        : undefined;
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      const current = updatedTransfers[index];
      updatedTransfers[index] = {
        ...current,
        ...(cardRefundCap != null &&
        current.selectedAmountToTransfer > cardRefundCap
          ? { selectedAmountToTransfer: cardRefundCap }
          : {}),
        selectedPartyToTransferTo: selectedParty,
        // CLE-2458: a chosen refund card only applies to a patient refund; clear it otherwise.
        ...(selectedParty?.type === "patient"
          ? {}
          : { selectedRefundCardPaymentId: undefined }),
      };
      return updatedTransfers;
    });
  };

  const onRefundCardChange = (paymentId: string) => {
    // CLE-2458: clamp an already-entered amount down to the max refundable to the newly chosen card.
    // On Finix from a provider that's the provider ∩ card amount; otherwise the card's remaining charge.
    const newCard = refundableCards.find((c) => c.paymentId === paymentId);
    const cap = usesProviderCardCap
      ? getProviderCardReversibleInCents(
          transfer.selectedPartyToTransferFrom?.id,
          paymentId
        )
      : getCardRemainingInCents(newCard);
    // CLE-2997: and at what that card can take back from this provider's card sales.
    const cardRefundCap = toIsPatient
      ? getFinixCardRefund(transfer.selectedPartyToTransferFrom?.id, paymentId)
          ?.refundableInCents
      : undefined;
    // CLE-2993: from Clear Health on Finix, at what is left of that card's Clear Health leg.
    const clearHealthCap = getClearHealthRefundCap(paymentId);
    const caps = [cap, cardRefundCap, clearHealthCap].filter(
      (value): value is number => value != null
    );
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      const current = updatedTransfers[index];
      const clampedAmount = caps.length
        ? Math.min(current.selectedAmountToTransfer, ...caps)
        : current.selectedAmountToTransfer;
      updatedTransfers[index] = {
        ...current,
        selectedRefundCardPaymentId: paymentId,
        selectedAmountToTransfer: clampedAmount,
      };
      return updatedTransfers;
    });
  };
  const onAmountChange = (amount: number | null) => {
    let newAmount = amount || 0;
    const isClearHealth = transfer?.selectedPartyToTransferFrom?.id === null;
    const totalAllowable = transfer?.selectedPartyToTransferFrom?.totalAmount;
    // `totalAllowable != null`, not truthiness: a provider with $0.00 left has totalAllowable 0,
    // and a truthy check skipped the clamp. The InputNumber's `max` clamps only its DISPLAY, so
    // the field showed $0.00 while state (and the submit) kept the typed amount.
    if (
      !isClearHealth &&
      amount &&
      totalAllowable != null &&
      dollarsToCents(amount) > totalAllowable
    ) {
      newAmount = centsToDollars(totalAllowable);
    }
    // CLE-2458: never let a card-pinned refund exceed the max refundable to that card.
    if (
      selectedCardCapInCents != null &&
      dollarsToCents(newAmount) > selectedCardCapInCents
    ) {
      newAmount = centsToDollars(selectedCardCapInCents);
    }
    // CLE-2997: nor what can go back to the card from this provider.
    if (
      finixCardRefund &&
      dollarsToCents(newAmount) > finixCardRefund.refundableInCents
    ) {
      newAmount = centsToDollars(finixCardRefund.refundableInCents);
    }
    // CLE-2993: nor, from Clear Health on Finix, what Clear Health holds on the order.
    if (
      clearHealthRefundCapInCents != null &&
      dollarsToCents(newAmount) > clearHealthRefundCapInCents
    ) {
      newAmount = centsToDollars(clearHealthRefundCapInCents);
    }
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        selectedAmountToTransfer: dollarsToCents(newAmount),
      };
      return updatedTransfers;
    });
  };

  const onWithholdFeeChange = (checked: boolean) => {
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        withholdClearFee: checked,
      };
      return updatedTransfers;
    });
  };

  // Strict-freeze: until the authoritative rail is known, do not render the
  // rail-specific transfer options (which party is hidden differs by rail) —
  // otherwise a Stripe order on a Finix-flipped account would briefly hide
  // legitimate transfer parties / auto-clear a valid selection.
  if (isProviderLoading) {
    return (
      <div className="flex flex-column">
        <LoadingSpinner />
      </div>
    );
  }

  // Error-fallback policy: this form moves real money, so on a failed rail
  // lookup we fail closed rather than guess from the account flag (which is
  // wrong for a Stripe-era order on a flipped account). The server guards both
  // rails too, so a wrong submit would 409 — this just avoids a confusing form.
  if (isProviderError) {
    return (
      <div className="flex flex-column">
        <p className="small-text">
          Couldn&apos;t determine this order&apos;s payment method. Please
          reopen this dialog to retry before transferring funds.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-column">
      <div className="flex justify-content-between">
        <p className="font-semibold mb-2">Transfer {index + 1}</p>
        {showRemoveButton ? (
          <StyledButton
            data-testid="remove-transfer"
            icon="pi pi-times-circle"
            className="p-button-text p-button-danger p-0 mb-0"
            style={{ alignSelf: "end" }}
            onClick={() => handleRemoveTransfer(index)}
          >
            <p className="small-text">Remove transfer</p>
          </StyledButton>
        ) : null}
      </div>
      <div className="flex flex-column">
        <Label htmlFor="from">From</Label>
        <Dropdown
          options={transferFromOptions}
          value={transfer.selectedPartyToTransferFrom}
          valueTemplate={valueTemplate}
          optionLabel="providerName"
          className="mt-2 mb-4"
          onChange={(e) => onTransferFromChange(e.value)}
          itemTemplate={itemTemplate}
          placeholder="Select party"
        />
        <Label htmlFor="to">To</Label>
        <Dropdown
          options={transferToOptions}
          value={transfer.selectedPartyToTransferTo}
          valueTemplate={valueTemplate}
          optionLabel="name"
          className="mt-2 mb-4"
          onChange={(e) => onTransferToChange(e.value)}
          placeholder="Select party"
          itemTemplate={itemTemplate}
          optionGroupLabel="label"
          optionGroupChildren="items"
          optionGroupTemplate={optionGroupTemplate}
        />
        <Label htmlFor="to">Amount</Label>
        <InputNumber
          value={centsToDollars(transfer.selectedAmountToTransfer)}
          max={
            amountMaxInCents != null
              ? centsToDollars(amountMaxInCents)
              : undefined
          }
          onChange={(e) => onAmountChange(e.value)}
          disabled={
            !transfer?.selectedPartyToTransferFrom || sourceHasNothingAvailable
          }
          min={0}
          currency="USD"
          locale="en-US"
          mode="currency"
          inputId="amountToTransfer"
          className={
            sourceHasNothingAvailable ||
            cardRefundIsLimited ||
            clearHealthRefundCapInCents != null
              ? "mt-2"
              : "mt-2 mb-4"
          }
        />
        {clearHealthRefundCapInCents != null && (
          <small className="mb-4 mt-1" data-testid="clear-health-refund-limit">
            {describeFinixClearHealthRefundLimit(clearHealthRefundCapInCents)}
          </small>
        )}
        {cardRefundIsLimited && finixCardRefund ? (
          <small
            className={`mb-4 mt-1${
              sourceHasNothingAvailable ? " p-error" : ""
            }`}
            data-testid="card-refund-limit"
          >
            {describeFinixCardRefundLimit(
              transfer.selectedPartyToTransferFrom?.name ?? "This provider",
              finixCardRefund
            )}
          </small>
        ) : (
          sourceHasNothingAvailable && (
            <small className="mb-4 mt-1 p-error">
              {`${transfer.selectedPartyToTransferFrom?.name} has ${parsePrice(
                centsToDollars(amountMaxInCents ?? 0)
              )} available to transfer.`}
            </small>
          )
        )}
        {showRefundCardPicker && (
          <>
            <Label htmlFor="refundCard">Refund to card</Label>
            <Dropdown
              inputId="refundCard"
              options={refundableCards.map((card) => {
                const takenByAnotherTransfer =
                  cardsChosenByOtherTransfers.includes(card.paymentId);
                // On Finix the card's own charges aren't the refund limit (the provider slice is), so
                // showing a card amount here is misleading — omit it and let the helper text below
                // state the real per-provider max. On Stripe the card's REMAINING refundable IS the
                // cap, so show that; fall back to the gross charged if the server couldn't read it.
                const remaining = getCardRemainingInCents(card);
                const cardAmountLabel = useFinix
                  ? ""
                  : ` — ${parsePrice(
                      centsToDollars(remaining ?? card.amountInCents)
                    )}`;
                return {
                  label: `${card.brand || "Card"}${
                    card.last4 ? ` •••• ${card.last4}` : ""
                  }${cardAmountLabel}${
                    takenByAnotherTransfer ? " (already selected above)" : ""
                  }`,
                  value: card.paymentId,
                  disabled: takenByAnotherTransfer,
                };
              })}
              optionDisabled="disabled"
              value={transfer.selectedRefundCardPaymentId}
              onChange={(e) => onRefundCardChange(e.value)}
              placeholder="Default (largest card first, may span both)"
              className="mt-2"
              showClear
            />
            {selectedRefundCard ? (
              <small className="mb-4 mt-1">
                {selectedCardCapInCents != null
                  ? `Up to ${parsePrice(
                      centsToDollars(selectedCardCapInCents)
                    )} can be refunded to this card${
                      usesProviderCardCap ? " from the selected provider" : ""
                    }.`
                  : "Select who to refund from above to see how much can be refunded to this card."}
              </small>
            ) : (
              <small className="mb-4 mt-1">
                No specific card chosen — refunds the largest card first, then
                the other card if the amount is larger.
              </small>
            )}
          </>
        )}
        {isSuperAdmin &&
          transfer.selectedPartyToTransferFrom?.id === null &&
          transfer.selectedPartyToTransferTo?.id !== null &&
          transfer.selectedPartyToTransferTo?.type === "provider" && (
            <div className="flex align-items-center mb-4">
              <Checkbox
                inputId={`withholdClearFee-${index}`}
                checked={transfer.withholdClearFee || false}
                onChange={(e) => onWithholdFeeChange(e.checked || false)}
              />
              <label htmlFor={`withholdClearFee-${index}`} className="ml-2">
                Withhold clear fee
              </label>
            </div>
          )}
      </div>
    </div>
  );
}
